import { cn } from '@/lib/utils';
import { MessageContext } from 'contexts/MessageContext';
import { RotateCcw, Star } from 'lucide-react';
import { memo, useContext, useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  IMessageElement,
  IStep,
  messagesState,
  unsentMessagesState,
  useChatInteract,
  useConfig
} from '@chainlit/react-client';

import AutoResizeTextarea from '@/components/AutoResizeTextarea';
import { Pencil } from '@/components/icons/Pencil';
import { Button } from '@/components/ui/button';
import { Translator } from 'components/i18n';

import { InlinedElements } from './Content/InlinedElements';

interface Props {
  message: IStep;
  elements: IMessageElement[];
}

const UserMessage = memo(function UserMessage({
  message,
  elements,
  children
}: React.PropsWithChildren<Props>) {
  const { askUser, loading, editable } = useContext(MessageContext);
  const { editMessage, toggleMessageFavorite, resendMessage } =
    useChatInteract();
  // The server never said this question arrived (its socket had died
  // quietly). Say so where the user is looking, and keep it one tap away.
  const unsent = useRecoilValue(unsentMessagesState)[message.id];
  const { config } = useConfig();
  const setMessages = useSetRecoilState(messagesState);
  const disabled = loading || !!askUser;
  const isFavorite = message.metadata?.favorite === true;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const inlineElements = useMemo(() => {
    return elements.filter(
      (el) => el.forId === message.id && el.display === 'inline'
    );
  }, [message.id, elements]);
  const favoritesEnabled = !!config?.features?.favorites;

  const handleEdit = () => {
    if (editValue) {
      setMessages((prev) => {
        const index = prev.findIndex((m) => m.id === message.id);
        if (index === -1) {
          return prev;
        }
        const slice = prev.slice(0, index + 1);
        slice[index].steps = [];
        return slice;
      });
      setIsEditing(false);
      editMessage({ ...message, output: editValue });
    }
  };

  return (
    <div className="flex flex-col w-full gap-1">
      <InlinedElements elements={inlineElements} className="items-end" />

      <div className="flex flex-row items-center gap-1 w-full group">
        <div className="ml-auto" />
        {!isEditing && editable && (
          <Button
            variant="ghost"
            size="icon"
            className="edit-message invisible group-hover:visible"
            onClick={() => {
              setEditValue(message.output);
              setIsEditing(true);
            }}
            disabled={disabled}
          >
            <Pencil />
          </Button>
        )}
        {!isEditing && favoritesEnabled && (
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'favorite-message invisible group-hover:visible',
              isFavorite ? 'visible text-yellow-500' : 'text-muted-foreground'
            )}
            onClick={() => toggleMessageFavorite(message)}
            disabled={disabled}
          >
            <Star className={cn('h-4 w-4', isFavorite ? 'fill-current' : '')} />
          </Button>
        )}
        {/* The notched bottom corner gives the bubble a speaker without the
            24px pill radius, and the border keeps it legible on white now that
            the fill is a tint rather than a grey. */}
        <div
          dir="auto"
          className={cn(
            'relative px-[15px] py-[9px] text-[14.5px] font-medium leading-[1.6]',
            inlineElements.length ? 'rounded-tr-lg' : '',
            isEditing
              ? 'w-full flex-grow'
              : 'max-w-[75%] max-sm:max-w-[88%] flex-grow-0',
            ''
          )}
          style={{
            background: 'var(--sig-table-head)',
            border: '1px solid var(--sig-border)',
            borderRadius: '16px 16px 4px 16px',
            color: 'var(--sig-ink)'
          }}
        >
          {isEditing ? (
            <div className="bg-accent flex flex-col">
              <AutoResizeTextarea
                id="edit-chat-input"
                autoFocus
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="mt-1 bg-transparent placeholder:text-base placeholder:font-medium text-base"
                maxHeight={250}
              />
              <div className="flex justify-end gap-4">
                <Button variant="ghost" onClick={() => setIsEditing(false)}>
                  <Translator path="common.actions.cancel" />
                </Button>
                <Button
                  className="confirm-edit"
                  disabled={disabled}
                  onClick={handleEdit}
                >
                  <Translator path="common.actions.confirm" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              {message.command ? (
                <div className="font-bold text-[#08f] command-span">
                  {message.command}
                </div>
              ) : null}
              {children}
            </div>
          )}
        </div>
      </div>
      {unsent && !isEditing ? (
        <div
          role="alert"
          className="flex items-center justify-end gap-2 text-[12.5px] text-red-600 dark:text-red-400"
        >
          <span>Not sent — the connection dropped.</span>
          <button
            type="button"
            onClick={() => resendMessage(message.id)}
            className="inline-flex items-center gap-1 font-semibold underline underline-offset-2"
          >
            <RotateCcw className="h-3 w-3" aria-hidden="true" />
            Retry
          </button>
        </div>
      ) : null}
    </div>
  );
});

export default UserMessage;
